import { Inject, Injectable, ViewContainerRef } from "@angular/core";
import { Graph } from "@maxgraph/core";
import { CompareFlatObjectPipe } from "@nms-ng2/app/shared/pipes/compare-flat-object.pipe";
import { DwdmDiagramAbstractConverterAmplifiers } from "../../diagram/converter/dwdm-diagram-abstract-converter-amplifiers";
import { DwdmDiagramConverterHelper } from "../../diagram/converter/dwdm-diagram-converter-helper";
import { AmplifiersConverterData } from "../../diagram/converter/dwdm-diagram-converter.models";
import { AmplifierDirectionMap, AmplifierElement, CustomCell } from "../../diagram/models/diagram.models";
import { PopoverDataService } from "../../diagram/popover/popover-data.service";
import { PopoverVisibilityManagerService } from "../../diagram/popover/popover-visibility-manager.service";
import { AmplifierDiagnosticData, DwdmCircuitDiagnosticData } from "../../dwdm-circuit.model";
import { Amplifier } from "../../wizard/steps/5-amplifiers/amplifiers-step.model";
import { AlarmSeverity, DiagramCardAlarm, DiagramCardData, DiagramCardInformation } from "@nms-angular-toolkit/nms-diagram-card";
import { PopoverHelperService } from "../../diagram/popover/popover-helper.service";
import { ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";

/**
 * Responsável por converter @see AmplifiersConverterData em @see AmplifierDirectionMap.
 */
@Injectable({
    providedIn: "root"
})
export class DwdmDiagramDiagnosticConverterAmplifiersService extends DwdmDiagramAbstractConverterAmplifiers {

    private readonly translatedDiagramCardField: { [key in string]: string };

    constructor(private readonly buildElementPopoverService: PopoverDataService,
        private readonly popoverVisibilityManagerService: PopoverVisibilityManagerService,
        private readonly compareFlatObjectPipe: CompareFlatObjectPipe,
        private readonly popoverHelperService: PopoverHelperService,
        @Inject(ANGULARJS_TRANSLATE) private readonly translate: any) {
        super();
        this.translatedDiagramCardField = {
            INPUT_POWER: translate.instant("dwdm.circuit.modal.diagnostic.table.input.power"),
            OUTPUT_POWER: translate.instant("dwdm.circuit.modal.diagnostic.table.output.power"),
            SIGNAL_POWER: translate.instant("dwdm.circuit.modal.diagnostic.table.signal.power"),
            GAIN: translate.instant("dwdm.circuit.modal.diagnostic.table.gain"),
            AMPLIFIER: translate.instant("dwdm.circuit.modal.diagnostic.table.amplifier"),
            LD_CURRENT: translate.instant("dwdm.circuit.modal.diagnostic.table.ld.current"),
            LD_EOL_CURRENT: translate.instant("dwdm.circuit.modal.diagnostic.table.ld.eol.current"),
            TEC_CURRENT: translate.instant("dwdm.circuit.modal.diagnostic.table.tec.current"),
            TEC_VOLTAGE: translate.instant("dwdm.circuit.modal.diagnostic.table.tec.voltage"),
            SUPPLY_VOLTAGE: translate.instant("dwdm.circuit.modal.diagnostic.table.supply.voltage"),
            LD_TEMPERATURE: translate.instant("dwdm.circuit.modal.diagnostic.table.ld.temperature"),
            CASE_TEMPERATURE: translate.instant("dwdm.circuit.modal.diagnostic.table.case.temperature")
        };
    }

    public convert(amplifiersData: AmplifiersConverterData, dwdmCircuitDiagnosticData: DwdmCircuitDiagnosticData,
        diagnosticModalViewRef: ViewContainerRef): AmplifierDirectionMap {

        return super.convertData(amplifiersData, (amplifier: Amplifier, amplifierData: any) => {
            const { direction, amplifierType, state, name } = amplifierData;

            const diagramAmplifierElement: AmplifierElement = {
                id: DwdmDiagramConverterHelper.createId(),
                direction,
                type: amplifierType,
                name,
                state
            };

            diagramAmplifierElement.popoverData = this.getPopoverData(dwdmCircuitDiagnosticData, amplifier);
            diagramAmplifierElement.buildPopover = (graph: Graph, cell: CustomCell) => {
                const target = this.buildElementPopoverService.create(graph, cell, cell.popoverData);

                this.popoverVisibilityManagerService.show(diagnosticModalViewRef, target);
            };

            return diagramAmplifierElement;
        });
    }

    private getPopoverData(dwdmCircuitDiagnosticData: DwdmCircuitDiagnosticData, amplifier: Amplifier): DiagramCardData {
        const data = dwdmCircuitDiagnosticData.amplifiers.find((amplifierDiagnostic: AmplifierDiagnosticData) =>
            this.compareFlatObjectPipe.transform(amplifierDiagnostic.portIdentifier, amplifier.portIdentifier)
        );

        const cardData = new DiagramCardData();
        cardData.title = `edfa ${data.edfaId}`;
        cardData.informationBlocks = this.buildInformationBlocks(data);
        cardData.alarms = this.buildAlarms(data);

        return cardData;
    }

    private buildInformationBlocks(data: AmplifierDiagnosticData): DiagramCardInformation[] {
        const inputPower = this.popoverHelperService.getTextOrPlaceHolder(data.inputPower);
        const outputPower = this.popoverHelperService.getTextOrPlaceHolder(data.outputPower);
        const signalPower = this.popoverHelperService.getTextOrPlaceHolder(data.signalPower);
        const gain = this.popoverHelperService.getTextOrPlaceHolder(data.gain);
        const amplifier = this.popoverHelperService.getTextOrPlaceHolder(data.amplifier);
        const ldCurrent = this.popoverHelperService.getTextOrPlaceHolder(data.ldCurrent);
        const ldEolCurrent = this.popoverHelperService.getTextOrPlaceHolder(data.ldEolCurrent);
        const tecCurrent = this.popoverHelperService.getTextOrPlaceHolder(data.tecCurrent);
        const tecVoltage = this.popoverHelperService.getTextOrPlaceHolder(data.tecVoltage);
        const supplyVoltage = this.popoverHelperService.getTextOrPlaceHolder(data.supplyVoltage);
        const ldTemperature = this.popoverHelperService.getTextOrPlaceHolder(data.ldTemperature);
        const caseTemperature = this.popoverHelperService.getTextOrPlaceHolder(data.caseTemperature);

        return [
            {
                [this.translatedDiagramCardField.INPUT_POWER]: inputPower,
                [this.translatedDiagramCardField.OUTPUT_POWER]: outputPower,
                [this.translatedDiagramCardField.SIGNAL_POWER]: signalPower,
                [this.translatedDiagramCardField.GAIN]: gain,
                [this.translatedDiagramCardField.AMPLIFIER]: amplifier,
                [this.translatedDiagramCardField.LD_CURRENT]: ldCurrent
            },
            {
                [this.translatedDiagramCardField.LD_EOL_CURRENT]: ldEolCurrent,
                [this.translatedDiagramCardField.TEC_CURRENT]: tecCurrent,
                [this.translatedDiagramCardField.TEC_VOLTAGE]: tecVoltage,
                [this.translatedDiagramCardField.SUPPLY_VOLTAGE]: supplyVoltage,
                [this.translatedDiagramCardField.LD_TEMPERATURE]: ldTemperature,
                [this.translatedDiagramCardField.CASE_TEMPERATURE]: caseTemperature
            }
        ];
    }

    private buildAlarms(data: AmplifierDiagnosticData): DiagramCardAlarm[] {
        return data.alarms?.map(alarm => {
            const { information, severity } = alarm;
            const cardSeverity = this.popoverHelperService.normalizeCardSeverity(severity) as AlarmSeverity;

            return { severity: cardSeverity, message: information };
        });
    }

}
